/* PMX: Tech Overlay
   Reveals the technical overlay art when the panel scrolls into view, builds
   the elbow leader lines, and types out monospace lines flagged with data-type.

   Everything except the leader lines is CSS-driven, so a JS failure still
   leaves a fully readable, fully styled panel — it just loses the decorative
   (aria-hidden) connector lines. */
(function () {
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SVG_NS = 'http://www.w3.org/2000/svg';

  /* ------------------------------------------------------------- leaders -- */

  /* The leader SVG is given a viewBox equal to the layer's pixel size so one
     user unit is one pixel. That keeps stroke width a true hairline and lets
     the dash pattern be measured in px — the combination of a stretched
     viewBox and vector-effect:non-scaling-stroke computes dashes in screen
     space and shatters the draw-on into hundreds of 1px dashes. */
  function buildLeaders(root) {
    var svg = root.querySelector('.pmx-techov__lines');
    var layer = root.querySelector('.pmx-techov__layer');
    if (!svg || !layer) return;

    var w = layer.clientWidth;
    var h = layer.clientHeight;
    if (!w || !h) return;

    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);

    while (svg.firstChild) svg.removeChild(svg.firstChild);

    var specs = root.querySelectorAll('[data-leader]');
    var revealed = root.classList.contains('is-in');

    var isMobile = window.matchMedia('(max-width: 991px)').matches;

    for (var i = 0; i < specs.length; i++) {
      var spec = specs[i];

      // The callout this line points from is hidden at this breakpoint, so a
      // line to it would dangle in mid-air.
      if (spec.offsetParent === null) continue;

      var d = spec.dataset;
      var fromX = isMobile && d.leaderFromXMb ? d.leaderFromXMb : d.leaderFromX;
      var fromY = isMobile && d.leaderFromYMb ? d.leaderFromYMb : d.leaderFromY;
      var x = (parseFloat(fromX) / 100) * w;
      var y = (parseFloat(fromY) / 100) * h;
      var tx = (parseFloat(d.leaderX) / 100) * w;
      var ty = (parseFloat(d.leaderY) / 100) * h;
      var pts;

      switch (d.leader) {
        case 'h_first':
          pts = [[x, y], [tx, y], [tx, ty]];
          break;
        case 'v_first':
          pts = [[x, y], [x, ty], [tx, ty]];
          break;
        case 'h_diag':
          pts = [[x, y], [x + (tx - x) * 0.55, y], [tx, ty]];
          break;
        default:
          pts = [[x, y], [tx, ty]];
      }

      var line = document.createElementNS(SVG_NS, 'polyline');
      line.setAttribute(
        'points',
        pts
          .map(function (p) {
            return p[0].toFixed(1) + ',' + p[1].toFixed(1);
          })
          .join(' ')
      );
      line.setAttribute('class', 'pmx-techov__lead' + (d.leaderAccent === 'true' ? ' pmx-techov__lead--accent' : ''));
      svg.appendChild(line);

      var len = line.getTotalLength();
      line.style.strokeDasharray = len;
      line.style.transitionDelay = (parseInt(d.leaderDelay, 10) || 0) + 'ms';

      if (revealed || REDUCED.matches) {
        // Already on screen (resize, or the theme editor re-rendering): land
        // the line at its final state rather than replaying the draw-on.
        line.style.transition = 'none';
        line.style.strokeDashoffset = 0;
      } else {
        line.style.strokeDashoffset = len;
      }
    }
  }

  function drawLeaders(root) {
    var lines = root.querySelectorAll('.pmx-techov__lines polyline');
    for (var i = 0; i < lines.length; i++) lines[i].style.strokeDashoffset = 0;
  }

  /* ---------------------------------------------------------- typewriter -- */

  function typeLine(el) {
    var text = el.getAttribute('data-text') || '';
    var delay = parseInt(el.getAttribute('data-delay'), 10) || 0;
    var speed = parseInt(el.getAttribute('data-speed'), 10) || 26;

    if (REDUCED.matches) {
      el.textContent = text;
      return;
    }

    el.textContent = '';
    window.setTimeout(function () {
      var i = 0;
      el.classList.add('is-typing');
      (function step() {
        el.textContent = text.slice(0, i);
        if (i >= text.length) {
          // Hold the caret briefly so the line lands, then drop it.
          window.setTimeout(function () {
            el.classList.remove('is-typing');
          }, 900);
          return;
        }
        i += 1;
        window.setTimeout(step, speed);
      })();
    }, delay);
  }

  /* --------------------------------------------------------------- wiring -- */

  function reveal(root) {
    if (root.classList.contains('is-in')) return;
    root.classList.add('is-in');
    drawLeaders(root);

    var lines = root.querySelectorAll('.pmx-techov__callout li[data-type]');
    for (var i = 0; i < lines.length; i++) typeLine(lines[i]);
  }

  function init(root) {
    if (!root || root.dataset.pmxTechovReady === 'true') return;
    root.dataset.pmxTechovReady = 'true';

    // Stash the copy and blank the line so it isn't readable before its turn.
    var lines = root.querySelectorAll('.pmx-techov__callout li[data-type]');
    for (var i = 0; i < lines.length; i++) {
      lines[i].setAttribute('data-text', lines[i].textContent.trim());
      if (!REDUCED.matches) lines[i].textContent = '';
    }

    buildLeaders(root);

    var resizeTimer;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        buildLeaders(root);
      }, 150);
    });

    if (!('IntersectionObserver' in window)) {
      reveal(root);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var j = 0; j < entries.length; j++) {
          if (entries[j].isIntersecting) {
            reveal(entries[j].target);
            observer.unobserve(entries[j].target);
          }
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(root);
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-pmx-techov]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: sections re-render in place, so re-bind and show at once.
  document.addEventListener('shopify:section:load', function (event) {
    var nodes = event.target.querySelectorAll('[data-pmx-techov]');
    for (var i = 0; i < nodes.length; i++) {
      init(nodes[i]);
      reveal(nodes[i]);
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest('[data-pmx-techov]');
    if (root) reveal(root);
  });
})();
